import { del, get, post, toQueryParams } from '@/lib/http';
import { endpoints } from './endpoints';
import type {
  Carton,
  CartonReceipt,
  CartonTimelineEvent,
  CurrentDispatchLotResponse,
  CurrentLotResponse,
  DispatchCartonRequest,
  DispatchLotProgress,
  DispatchLotStatus,
  DispatchLotSummary,
  DispatchRecord,
  GenerateLotRequest,
  LotProgress,
  LotSummary,
  Page,
  PageRequest,
  ReceiveCartonRequest,
  ScanHistoryItem,
  ScanLotCartonResponse,
  ScanType,
  SetDispatchLotOpenRequest,
  ValidateDispatchResponse,
  ValidateReceiveRequest,
  ValidateReceiveResponse,
  QrStatus,
} from '@/types';

export interface CartonQuery extends PageRequest {
  status?: QrStatus | 'ALL';
  buyerId?: number;
  poId?: number;
  batchId?: number;
  warehouseId?: number;
  lotId?: number;
  search?: string;
}

export interface ReceivedLotQuery extends PageRequest {
  buyerId?: number;
  poId?: number;
  status?: 'OPEN' | 'CLOSED' | 'ALL';
  search?: string;
}

export interface DispatchedLotQuery extends PageRequest {
  buyerId?: number;
  poId?: number;
  status?: DispatchLotStatus | 'ALL';
  search?: string;
}

export interface ScanHistoryQuery extends PageRequest {
  type?: ScanType | 'ALL';
  search?: string;
  /** restrict to the signed-in user's own scans (mobile "My history") */
  mine?: boolean;
}

export const cartonApi = {
  list: (query: CartonQuery = {}) =>
    get<Page<Carton>>(endpoints.cartons.list, {
      params: toQueryParams({ page: 0, size: 10, ...query }),
    }),

  byCode: (code: string) => get<Carton>(endpoints.cartons.byCode(code)),

  timeline: (code: string) => get<CartonTimelineEvent[]>(endpoints.cartons.timeline(code)),

  /* ----------------------------- receiving ------------------------------- */

  /** Step 3 of the receiver flow: confirm the buyer QR and internal QR match. */
  validateReceive: (body: ValidateReceiveRequest) =>
    post<ValidateReceiveResponse, ValidateReceiveRequest>(endpoints.cartons.validateReceive, body),

  receive: (body: ReceiveCartonRequest) =>
    post<CartonReceipt, ReceiveCartonRequest>(endpoints.cartons.receive, body),

  /* ------------------------- /receive lot workflow ------------------------ */

  /** The lot currently mid-scan, if any — backs resume-on-login. */
  currentLot: () => get<CurrentLotResponse>(endpoints.cartons.lotCurrent),

  /** Generates blank cartons against a lot — no scanning involved yet. */
  generateLot: (body: GenerateLotRequest) =>
    post<LotProgress, GenerateLotRequest>(endpoints.cartons.lots, body),

  /** One barcode scan — fills the next unscanned carton in the lot. */
  scanLotCarton: (lotId: number, code: string) =>
    post<ScanLotCartonResponse, { code: string }>(endpoints.cartons.lotScan(lotId), { code }),

  /** The "Received Lots" list — every lot the /receive workflow has generated cartons against. */
  receivedLots: (query: ReceivedLotQuery = {}) =>
    get<Page<LotSummary>>(endpoints.cartons.lots, {
      params: toQueryParams({ page: 0, size: 10, ...query }),
    }),

  /** Deletes a lot and every carton generated against it — bulk delete from the Received Lots list. */
  deleteLot: (lotId: number) => del<void>(endpoints.cartons.lot(lotId)),

  receipts: (query: CartonQuery = {}) =>
    get<Page<CartonReceipt>>(endpoints.cartons.receipts, {
      params: toQueryParams({ page: 0, size: 10, ...query }),
    }),

  /* ----------------------------- dispatching ----------------------------- */

  /** The lot currently holding the dispatch slot, if any — backs the dispatch stations' progress banner. */
  currentDispatchLot: () => get<CurrentDispatchLotResponse>(endpoints.cartons.dispatchLotCurrent),

  /** The "Dispatched Lots" list — every lot with at least one carton dispatched. Read-only. */
  dispatchedLots: (query: DispatchedLotQuery = {}) =>
    get<Page<DispatchLotSummary>>(endpoints.cartons.dispatchLots, {
      params: toQueryParams({ page: 0, size: 10, ...query }),
    }),

  /**
   * Manually claims or releases a lot's dispatch slot — a dispatcher can pause
   * a partially dispatched lot and resume it later, any number of times.
   */
  setDispatchLotOpen: (lotId: number, open: boolean) =>
    post<DispatchLotProgress, SetDispatchLotOpenRequest>(endpoints.cartons.dispatchLotOpen(lotId), { open }),

  /** Looks up the carton + its planned shipment from a single internal QR scan. */
  validateDispatch: (internalQrCode: string) =>
    post<ValidateDispatchResponse, { internalQrCode: string }>(
      endpoints.cartons.validateDispatch,
      { internalQrCode },
    ),

  dispatch: (body: DispatchCartonRequest) =>
    post<DispatchRecord, DispatchCartonRequest>(endpoints.cartons.dispatch, body),

  dispatches: (query: CartonQuery = {}) =>
    get<Page<DispatchRecord>>(endpoints.cartons.dispatches, {
      params: toQueryParams({ page: 0, size: 10, ...query }),
    }),

  /* ------------------------------- misc ---------------------------------- */

  scanHistory: (query: ScanHistoryQuery = {}) =>
    get<Page<ScanHistoryItem>>(endpoints.scanHistory, {
      params: toQueryParams({ page: 0, size: 20, ...query }),
    }),
};
